import {isoCountryNames, Mapbox, MapboxAddress, MapboxCoordinates} from "../../../../@bizkeytech";
import {MenuItem, TextField, Unstable_Grid2 as Grid, useMediaQuery, useTheme} from "@mui/material";
import {useFieldControl} from "../control";
import React, {useEffect, useMemo, useState} from "react";

export type FieldMapValue = Partial<MapboxAddress & MapboxCoordinates>;

export interface FieldMapProps {
}

const FieldMap = (props: FieldMapProps) => {
    const fieldControl = useFieldControl<FieldMapValue>();
    const [textFieldsAddress, setTextFieldsAddress] = useState<MapboxAddress>({
        address: '',
        province: '',
        country: '',
        postalCode: '',
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    /**
     * With ech change in the address segment of the address value:
     * - updates the textFieldsAddress state.
     */
    useEffect(() => {
        setTextFieldsAddress(prevState => ({...prevState, address: fieldControl.value?.address ?? ''}));
    }, [fieldControl.value?.address]);

    /**
     * With ech change in the province segment of the address value:
     * - updates the textFieldsAddress state.
     */
    useEffect(() => {
        setTextFieldsAddress(prevState => ({...prevState, province: fieldControl.value?.province ?? ''}));
    }, [fieldControl.value?.province]);

    /**
     * With ech change in the country segment of the address value:
     * - updates the textFieldsAddress state.
     */
    useEffect(() => {
        setTextFieldsAddress(prevState => ({...prevState, country: fieldControl.value?.country ?? ''}));
    }, [fieldControl.value?.country]);

    /**
     * With ech change in the postalCode segment of the address value:
     * - updates the textFieldsAddress state.
     */
    useEffect(() => {
        setTextFieldsAddress(prevState => ({...prevState, postalCode: fieldControl.value?.postalCode ?? ''}));
    }, [fieldControl.value?.postalCode]);

    const startingAddress = useMemo<MapboxAddress | undefined>(() => !fieldControl.value
        ? undefined
        : ({
            address: fieldControl.value?.address!,
            province: fieldControl.value?.province!,
            country: fieldControl.value?.country!,
            postalCode: fieldControl.value?.postalCode!,
        }), [fieldControl.value])

    const startingCoordinates = useMemo<MapboxCoordinates | undefined>(() => !fieldControl.value
        ? undefined
        : ({
            lat: fieldControl.value?.lat!,
            lng: fieldControl.value?.lng!,
        }), [fieldControl.value])

    return (
        <>
            <Mapbox
                sx={{width: '100%', minHeight: 400, ...(fieldControl.erroneous && {borderColor: 'error.main'})}}
                {...(startingAddress ? {startingAddress: startingAddress} : startingCoordinates ? {startingCoordinates: startingCoordinates} : {})}
                onAddressSelect={(address) => fieldControl.setValue(prevState => ({
                    ...(prevState! ?? {}),
                    ...address,
                }))}
                onCoordinatesSelect={(coordinates) => fieldControl.setValue(prevState => ({
                    ...(prevState! ?? {}),
                    ...coordinates,
                }))}
            />
            <Grid container spacing={2} sx={{marginBlockStart: 1}}>
                <Grid xs={12}>
                    <TextField
                        label={'Address'}
                        fullWidth
                        variant={'outlined'}
                        value={textFieldsAddress.address ?? ""}
                        helperText={fieldControl.helperText}
                        error={fieldControl.erroneous}
                        sx={fieldControl.sx}
                        onChange={(e) => setTextFieldsAddress(prevState => ({...prevState, address: e.target.value ?? "",}))}
                        onBlur={(e) => fieldControl.setValue(prevState => ({
                            ...(prevState! ?? {}),
                            address: e.target.value ?? "",
                        }))}
                        disabled={fieldControl.disabled}
                        InputProps={{
                            readOnly: fieldControl.readonly,
                        }}
                    />
                </Grid>
                <Grid xs={12} md={'auto'}>
                    <TextField
                        label={'Province'}
                        variant={'outlined'}
                        fullWidth={isMobile}
                        value={textFieldsAddress.province ?? ""}
                        helperText={fieldControl.helperText}
                        error={fieldControl.erroneous}
                        sx={fieldControl.sx}
                        onChange={(e) => setTextFieldsAddress(prevState => ({...prevState, province: e.target.value ?? "",}))}
                        onBlur={(e) => fieldControl.setValue(prevState => ({
                            ...(prevState! ?? {}),
                            province: e.target.value ?? "",
                        }))}
                        disabled={fieldControl.disabled}
                        InputProps={{
                            readOnly: fieldControl.readonly,
                        }}
                    />
                </Grid>
                <Grid xs={12} md={'auto'}>
                    <TextField
                        select
                        label={'Country'}
                        variant={'outlined'}
                        fullWidth={isMobile}
                        value={textFieldsAddress.country ?? ""}
                        helperText={fieldControl.helperText}
                        error={fieldControl.erroneous}
                        sx={fieldControl.sx}
                        onChange={(e) => setTextFieldsAddress(prevState => ({...prevState, country: e.target.value ?? "",}))}
                        onBlur={(e) => fieldControl.setValue(prevState => ({
                            ...(prevState! ?? {}),
                            country: e.target.value ?? "",
                        }))}
                        disabled={fieldControl.disabled}
                        InputProps={{
                            readOnly: fieldControl.readonly,
                        }}
                        SelectProps={{
                            readOnly: fieldControl.readonly,
                            sx: {minWidth: 200},
                        }}
                    >
                        {
                            isoCountryNames.map((country) => (
                                <MenuItem key={country} value={country}>
                                    {country}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Grid>
                <Grid xs={12}>
                    <TextField
                        label={'Postal code'}
                        variant={'outlined'}
                        value={textFieldsAddress.postalCode ?? ""}
                        helperText={fieldControl.helperText}
                        error={fieldControl.erroneous}
                        sx={fieldControl.sx}
                        fullWidth={isMobile}
                        onChange={(e) => setTextFieldsAddress(prevState => ({...prevState, postalCode: e.target.value ?? "",}))}
                        onBlur={(e) => fieldControl.setValue(prevState => ({
                            ...(prevState! ?? {}),
                            postalCode: e.target.value ?? "",
                        }))}
                        disabled={fieldControl.disabled}
                        InputProps={{
                            readOnly: fieldControl.readonly,
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
}

export default FieldMap;
